import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * A save a logged-out visitor asked for, held across the trip to the login page.
 *
 * WHY IT PERSISTS. Signing in is a full-page POST to the identity provider and back, so nothing in
 * memory survives it. This is the same zustand-`persist` shape `my-liked-colors-store` uses to hold
 * a guest's liked colours across the same journey, including its `_hasHydrated` gate: reading the
 * store before rehydration finishes reports an empty store, which would silently drop the very
 * action the member came back to complete.
 *
 * WHAT IT HOLDS, AND WHY SO LITTLE. Just enough to reopen the right thing for the right collection.
 * Not the colours, not the collection name: those are re-read from the server on return, so a
 * collection edited or unpublished while the member was signing in cannot be saved from a stale
 * snapshot.
 */
export const usePendingCollectionSaveStore = create()(
  persist(
    (set) => ({
      /** `{ action: "colours" | "collection", collectionId: number }`, or null. */
      pending: null,

      setPending: (pending) => set({ pending }),
      clearPending: () => set({ pending: null }),

      _hasHydrated: false,
      _setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),
    }),
    {
      onRehydrateStorage: (state) => {
        return () => state._setHasHydrated(true);
      },
      name: "valspar-paint-pending-collection-save",

      // Deliberately NOT persisted: it describes this page load, and writing it would restore a
      // "already hydrated" flag before rehydration has actually happened.
      partialize: (state) => ({ pending: state.pending }),
    }
  )
);
